import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';

const ROLE_REDIRECT = {
  client: '/dashboard',
  annotator: '/work',
  reviewer: '/review',
  admin: '/admin',
};

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('client');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(''); // set when login returns 403

  function switchMode(next) {
    setMode(next);
    setError('');
    setUnverifiedEmail('');
    setName('');
    setEmail('');
    setPassword('');
    setRole('client');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let userData;
      if (mode === 'login') {
        userData = await login(email, password);
      } else {
        userData = await register(name, email, password, role);
      }

      if (!userData.email_verified) {
        navigate('/verify-email', { state: { email } });
        return;
      }
      if ((userData.role === 'annotator' || userData.role === 'reviewer') && !userData.is_approved) {
        navigate('/pending-approval');
        return;
      }
      navigate(ROLE_REDIRECT[userData.role] || '/dashboard');
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'Please verify your email first') {
        setUnverifiedEmail(email);
        return;
      }
      const data = err.response?.data;
      const msg =
        data?.non_field_errors?.[0] ||
        data?.detail ||
        data?.email?.[0] ||
        data?.password?.[0] ||
        (mode === 'login' ? 'Login failed. Please check your credentials.' : 'Sign up failed. Please try again.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleResendVerification() {
    try {
      await client.post('/api/auth/resend-verification/', { email: unverifiedEmail });
      navigate('/verify-email', { state: { email: unverifiedEmail } });
    } catch {
      navigate('/verify-email', { state: { email: unverifiedEmail } });
    }
  }

  if (unverifiedEmail) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>AINA</h1>
          <div style={styles.verifyBanner}>
            <p style={{ margin: '0 0 12px', fontWeight: 600 }}>Email not verified</p>
            <p style={{ margin: '0 0 16px', fontSize: '0.9rem' }}>
              Please verify <strong>{unverifiedEmail}</strong> before logging in.
              Check your inbox or resend the link below.
            </p>
            <button style={styles.button} onClick={handleResendVerification}>
              Resend verification email
            </button>
            <button
              style={{ ...styles.button, background: 'transparent', color: '#666', marginTop: '8px' }}
              onClick={() => setUnverifiedEmail('')}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>AINA</h1>

        {/* Tab toggle */}
        <div style={styles.tabs}>
          <button
            type="button"
            style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}
            onClick={() => switchMode('login')}
          >
            Sign in
          </button>
          <button
            type="button"
            style={{ ...styles.tab, ...(mode === 'signup' ? styles.tabActive : {}) }}
            onClick={() => switchMode('signup')}
          >
            Sign up
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'signup' && (
            <>
              <label style={styles.label}>Full name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={styles.input}
                placeholder="Jane Doe"
              />
            </>
          )}

          <label style={styles.label}>Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
            placeholder="you@example.com"
          />

          <label style={styles.label}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={styles.input}
            placeholder="••••••••"
            minLength={mode === 'signup' ? 8 : undefined}
          />

          {mode === 'signup' && (
            <>
              <label style={styles.label}>Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={styles.input}
              >
                <option value="client">Client</option>
                <option value="annotator">Annotator</option>
                <option value="reviewer">Reviewer</option>
              </select>
            </>
          )}

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" disabled={loading} style={styles.button}>
            {loading
              ? mode === 'login' ? 'Signing in…' : 'Creating account…'
              : mode === 'login' ? 'Sign in' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#000',
  },
  card: {
    background: '#111',
    padding: '2rem',
    border: '1px solid #333',
    width: '100%',
    maxWidth: '380px',
  },
  title: { margin: '0 0 1rem', fontSize: '1.8rem', textAlign: 'center', color: '#fff' },
  tabs: {
    display: 'flex',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #333',
  },
  tab: {
    flex: 1,
    padding: '0.5rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    color: '#666',
    fontWeight: 500,
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
  },
  tabActive: {
    color: '#fff',
    borderBottom: '2px solid #fff',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { fontWeight: 600, fontSize: '0.875rem', color: '#aaa' },
  input: {
    padding: '0.6rem 0.75rem',
    border: '1px solid #444',
    background: '#000',
    color: '#fff',
    borderRadius: '4px',
    fontSize: '1rem',
    marginBottom: '0.5rem',
  },
  error: { color: '#ff6b6b', fontSize: '0.875rem', margin: '0.25rem 0' },
  button: {
    display: 'block',
    width: '100%',
    marginTop: '0.75rem',
    padding: '0.7rem',
    background: '#fff',
    color: '#000',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  verifyBanner: {
    background: '#1a1a1a',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '1.25rem',
    color: '#ccc',
    textAlign: 'center',
  },
};
